// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { compose } from '../../../../node_module_clones/recompose';
import { includes } from 'lodash';

// Utils
import * as pointLib from '../../../../common/maths/geometry/point';
import * as rectLib from '../../../../common/maths/geometry/rect';
import { getLineEndStyle, getLineStartStyle } from '../../../../common/elements/utils/elementPropertyUtils';
import { getControlPosition } from '../utils/lineControlPointUtil';
import {
    enforceLineMinLength,
    getAlignedColumnLineOrigins,
    getLineMinLength,
    getLinePointsOfInterest,
} from '../utils/lineUtil';

// Selectors
import { getElementId, getElement, getColourHex } from '../../selectors/elementSelector';
import getGridSize from '../../../utils/grid/gridSizeSelector';
import {
    makeGetLineEdgeDetails,
    getLineStartStyleSelector,
    getLineEndStyleSelector,
    getLineControlSelector,
    getLineEdge,
    getConnectedElementType,
} from '../lineSelector';
import { canvasZoomScaleSelector } from '../../../canvas/store/canvasZoomSelector';

// Components
import Line from '../Line';

// Measurements Store
import measurementsConnect from '../../../components/measurementsStore/measurementsConnect';
import { getShallowMeasurementsMap } from '../../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';

// Constants
import { LINE_EDGE } from '../../../../common/lines/lineConstants';
import { propIn } from '../../../../common/utils/immutableHelper';

// PERF: Don't need to connect to the specific measurements here, because they shouldn't change during the drag
const mapMeasurementsStateToProps = (connectionsState) => ({
    measurements: getShallowMeasurementsMap(connectionsState),
});

const getUpdatedEdges = () =>
    createSelector(
        getLineEdge(LINE_EDGE.start),
        getConnectedElementType(LINE_EDGE.start),
        getLineEdge(LINE_EDGE.end),
        getConnectedElementType(LINE_EDGE.end),
        makeGetLineEdgeDetails(),
        getLineControlSelector,
        getElement,
        getGridSize,
        canvasZoomScaleSelector,
        (state, ownProps) => ownProps.offsetDiff,
        (state, ownProps) => ownProps.scrollDiff,
        (state, ownProps) => ownProps.draggedElementIds,
        (state, ownProps) => ownProps.draggedDescendantIds,
        (
            startEdge,
            startElementType,
            endEdge,
            endElementType,
            lineDetails,
            control,
            element,
            gridSize,
            zoomScale,
            offsetDiff,
            scrollDiff,
            draggedElementIds,
            draggedDescendantIds,
        ) => {
            const { startEdgeOrigin, startConnectionRects, endEdgeOrigin, endConnectionRects } = lineDetails;

            const movedElementIds = draggedDescendantIds || draggedElementIds;
            const draggingStart = includes(movedElementIds, propIn(['content', LINE_EDGE.start, 'elementId'], element));
            const draggingEnd = includes(movedElementIds, propIn(['content', LINE_EDGE.end, 'elementId'], element));

            const unscaledOffsetDiff = pointLib.reverseScale(zoomScale, offsetDiff);
            const totalOffset = pointLib.translate(scrollDiff, unscaledOffsetDiff);

            const startElementRect =
                lineDetails.startElementRect && draggingStart
                    ? rectLib.translate(totalOffset, lineDetails.startElementRect)
                    : lineDetails.startElementRect;

            const endElementRect =
                lineDetails.endElementRect && draggingEnd
                    ? rectLib.translate(totalOffset, lineDetails.endElementRect)
                    : lineDetails.endElementRect;

            // If not already aligned for columns, then translate the dragged origins
            let translatedStartEdgeOrigin = draggingStart
                ? { ...startEdgeOrigin, ...pointLib.translate(totalOffset, startEdgeOrigin) }
                : startEdgeOrigin;
            let translatedEndEdgeOrigin = draggingEnd
                ? { ...endEdgeOrigin, ...pointLib.translate(totalOffset, endEdgeOrigin) }
                : endEdgeOrigin;

            const columnAlignmentResult = getAlignedColumnLineOrigins({
                startEdge,
                startElementType,
                startElementRect,
                endEdge,
                endElementType,
                endElementRect,
                gridSize,
                startEdgeOrigin: translatedStartEdgeOrigin,
                endEdgeOrigin: translatedEndEdgeOrigin,
            });

            translatedStartEdgeOrigin = columnAlignmentResult.startEdgeOrigin;
            translatedEndEdgeOrigin = columnAlignmentResult.endEdgeOrigin;

            const translatedStartConnectionRects = draggingStart
                ? rectLib.translateRects(totalOffset, startConnectionRects)
                : startConnectionRects;
            const translatedEndConnectionRects = draggingEnd
                ? rectLib.translateRects(totalOffset, endConnectionRects)
                : endConnectionRects;

            // Find the control point
            const controlPoint = getControlPosition({
                start: translatedStartEdgeOrigin,
                end: translatedEndEdgeOrigin,
                control,
                gridSize,
            });

            const linePois = getLinePointsOfInterest({
                startEdgeOrigin: translatedStartEdgeOrigin,
                startConnectionRects: translatedStartConnectionRects,
                endEdgeOrigin: translatedEndEdgeOrigin,
                endConnectionRects: translatedEndConnectionRects,
                controlPoint,
            });

            const startStyle = getLineStartStyle(element);
            const endStyle = getLineEndStyle(element);
            const minLength = getLineMinLength(startStyle, endStyle, gridSize);

            return enforceLineMinLength(linePois, { minLength });
        },
    );

const makeMapStateToProps = () =>
    createSelector(
        getElementId,
        getElement,
        getUpdatedEdges(),
        getColourHex(),
        getLineStartStyleSelector,
        getLineEndStyleSelector,
        (elementId, element, lineDetails, colorHex, startStyle, endStyle) => ({
            elementId,
            element,
            ...lineDetails,
            colorHex,
            startStyle,
            endStyle,
        }),
    );

const enhance = compose(measurementsConnect(mapMeasurementsStateToProps), connect(makeMapStateToProps));

const ConnectedLineDragPreview = (props) => <Line {...props} isPresentational />;

ConnectedLineDragPreview.propTypes = {
    elementId: PropTypes.string.isRequired,
    draggedElementIds: PropTypes.array.isRequired,
    draggedDescendantIds: PropTypes.array,
    offsetDiff: PropTypes.object,
};

export default enhance(ConnectedLineDragPreview);
